import React, { useCallback, useState } from "react";
import { Button, Col, Row, Typography } from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useTheme } from "shared-components/Styles/ThemeHook";
import InputWithPercentage from "./Inputs/InputWithPercentage";
import { currencyFormat, getPercentValues } from "./helpers";
import {
	MAX_VALUE,
	MID_VALUE,
	PERCENT_CREDIT_VALUE_HIGH,
	PERCENT_CREDIT_VALUE_LOW,
	PERCENT_OWN_RESOURCES_HIGH,
	PERCENT_OWN_RESOURCES_LOW,
} from "./constants";
import "./styles.less";

interface FormulaValues {
	percentOwnResources?: string;
	percentCreditValue?: string;
	ownResources: string;
	creditValue: string;
	formula: number;
}

export type FinanceCardProps = {
	initPrice: string;
	quotaMonthly: string;
};

const DESKTOP_SOURCE = 0;
const MOBILE_SOURCE = 4;

const CredifamiliaFinancing = ({
	showFinancingCard,
	financingRef,
	price,
}) => {
	const screen = useBreakpoint();
	const { theme } = useTheme();
	const source = screen.lg ? DESKTOP_SOURCE : MOBILE_SOURCE;

	const {
		ownResources: initOwnResources,
		creditValue: initCreditValue,
		formula: result,
	} = getPercentValues({
		propertyPrice: price,
		percentOwnResources:
			price <= MID_VALUE ? PERCENT_OWN_RESOURCES_LOW : PERCENT_OWN_RESOURCES_HIGH,
		percentCreditValue:
			price <= MID_VALUE ? PERCENT_CREDIT_VALUE_LOW : PERCENT_CREDIT_VALUE_HIGH,
	});

	const [formulaValues, setFormulaValues] = useState<FormulaValues>({
		percentOwnResources: String((Number(initOwnResources) / Number(price)) * 100),
		percentCreditValue: String((Number(initCreditValue) / Number(price)) * 100),
		ownResources: initOwnResources,
		creditValue: initCreditValue,
		formula: result,
	});

	const isBelowMinimumResources =
		(Number(formulaValues.percentOwnResources) / 100 < PERCENT_OWN_RESOURCES_LOW &&
			price <= MID_VALUE) ||
		(Number(formulaValues.percentOwnResources) / 100 < PERCENT_OWN_RESOURCES_HIGH &&
			price > MID_VALUE &&
			price <= MAX_VALUE);

	const handleSimulatorValues = useCallback(
		(value: string, name: string, type: string): void => {
			if (name === "ownResources") {
				if (type === "percentage") {
					const percentageValue = 100 - Number(value) >= 0 ? value : "0";
					const restPercentageValue =
						100 - Number(value) >= 0 ? String(100 - Number(value)) : "0";

					const { ownResources, creditValue, formula } = getPercentValues({
						propertyPrice: price,
						percentOwnResources: Number(percentageValue) / 100,
						percentCreditValue: Number(restPercentageValue) / 100,
					});

					setFormulaValues(prev => ({
						...prev,
						percentOwnResources: percentageValue,
						percentCreditValue: restPercentageValue,
						creditValue: creditValue,
						ownResources: ownResources,
						formula,
					}));
				} else {
					const resourcesNumber = Number(value.replaceAll(".", ""));
					if (resourcesNumber > price) return;
					const ownResources = resourcesNumber <= price ? resourcesNumber : price;
					const creditValue = price - resourcesNumber;

					const { percentOwnResources, formula, percentCreditValue } = getPercentValues({
						propertyPrice: price,
						percentOwnResources: Number(ownResources) / price,
						percentCreditValue: Number(creditValue) / price,
					});
					setFormulaValues(prev => ({
						...prev,
						ownResources: String(ownResources),
						creditValue: String(creditValue),
						percentOwnResources: String(Number(percentOwnResources) * 100),
						percentCreditValue: String(Number(percentCreditValue) * 100),
						formula,
					}));
				}
			}
			if (name === "creditValue") {
				if (type === "percentage") {
					const percentageValue = 100 - Number(value) >= 0 ? value : "0";
					const restPercentageValue =
						100 - Number(value) >= 0 ? String(100 - Number(value)) : "0";

					const {
						ownResources: creditValue,
						creditValue: ownResources,
						formula,
					} = getPercentValues({
						propertyPrice: price,
						percentOwnResources: Number(percentageValue) / 100,
						percentCreditValue: Number(restPercentageValue) / 100,
					});

					setFormulaValues(prev => ({
						...prev,
						percentCreditValue: percentageValue,
						percentOwnResources: restPercentageValue,
						creditValue: creditValue,
						ownResources: ownResources,
						formula,
					}));
				} else {
					const creditValueNumber = Number(value.replaceAll(".", ""));
					if (creditValueNumber > price) return;
					const creditValue = creditValueNumber <= price ? creditValueNumber : price;
					const ownResources = price - creditValueNumber;

					const { percentOwnResources, formula, percentCreditValue } = getPercentValues({
						propertyPrice: price,
						percentOwnResources: Number(ownResources) / price,
						percentCreditValue: Number(creditValue) / price,
					});

					setFormulaValues(prev => ({
						...prev,
						ownResources: String(ownResources),
						creditValue: String(creditValue),
						percentOwnResources: String(Number(percentOwnResources) * 100),
						percentCreditValue: String(Number(percentCreditValue) * 100),
						formula,
					}));
				}
			}
		},
		[price]
	);

	const onScrollToFinancing = () => {
		if (financingRef) {
			const componentFinancing = financingRef.current.offsetTop;
			const withAffixHeight = screen.lg ? 30 : 0;
			const scrollingTo = componentFinancing - withAffixHeight;
			window.scrollTo({ top: scrollingTo, behavior: "smooth" });
		}
	};

	if (showFinancingCard) {
		return (
			<div className="property-financing-card">
				<div className="header">
					Compra esta propiedad pagando tu cuota inicial de{" "}
					<b>${currencyFormat(formulaValues.ownResources)}</b> y cuotas de{" "}
					<b>${currencyFormat(formulaValues.formula.toFixed())}</b>
				</div>
				<div className="footer">
					<div className="logo">
						<img
							alt="Logo"
							src="/images/simulator/logo-credifamilia.webp"
							height={60}
							width={60}
						/>
						<span>Creando Oportunidades</span>
					</div>
					<Button onClick={onScrollToFinancing} className="btn">
						{" "}
						Simula tu credito{" "}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="property-financing">
			<Row gutter={[0, theme.spacing.mdSpacing]}>
				<Col span={24}>
					<Typography.Title level={4}>Financiación</Typography.Title>
				</Col>
				<Col xs={24} md={16}>
					<div className="logo">
						<img
							alt="Logo"
							src="/images/simulator/logo-credifamilia.webp"
							height={60}
							width={60}
						/>
						<span>Crédito Hipotecario 100% digital</span>
					</div>
					<Col xs={24} style={{ marginTop: "52px" }}>
						<InputWithPercentage
							label="Valor recurso propio o ahorros"
							name="ownResources"
							showIcon
							value={Number(formulaValues.ownResources).toFixed()}
							percentage={Number(formulaValues.percentOwnResources)}
							handleChange={handleSimulatorValues}
						/>
						<InputWithPercentage
							label="Valor del crédito"
							name="creditValue"
							value={Number(formulaValues.creditValue).toFixed()}
							percentage={Number(formulaValues.percentCreditValue)}
							handleChange={handleSimulatorValues}
						/>
					</Col>
				</Col>
				<Col xs={24} md={8} className="property-financing-monthly">
					{isBelowMinimumResources ? (
						<div className="financing-error">
							<Typography.Paragraph>
								{price <= MID_VALUE
									? "Tu recurso propio está por debajo del mínimo. Debes tener al menos el 25%"
									: "Tu recurso propio está por debajo del mínimo. Debes tener al menos el 30%"}
							</Typography.Paragraph>
						</div>
					) : (
						<div>
							<Typography.Title style={{ color: "#082645" }}>Valor primera cuota:</Typography.Title>
							<Typography.Title className="financing-price">
								${currencyFormat(formulaValues.formula.toFixed())}{" "}
							</Typography.Title>
							<a
								href="https://enlinea.credifamilia.com/inicio.xhtml?utm_source=fr_simulador?utm_campaign=fr_usada"
								target="_blank"
								rel="noopener noreferrer"
								className={"button-credifamilia"}>
								Solicita tu crédito
								<span style={{ marginLeft: "2px" }}> aquí</span>
							</a>
						</div>
					)}
				</Col>
			</Row>
		</div>
	);
};

export default CredifamiliaFinancing;

